import type { BillingPipelineItem, BillingPipelineView } from './data/billingApi.ts';
import type { FlowState } from './data/types';

export function assetPath(fileName: string): string {
  const base = import.meta.env.BASE_URL || '/';
  const normalizedBase = base.endsWith('/') ? base : `${base}/`;
  return `${normalizedBase}${fileName.replace(/^\//, '')}`;
}

export function getRuntimeInfo() {
  const slug = String(import.meta.env.VITE_APP_SLUG || 'field-service-technique').trim();
  const env = String(import.meta.env.VITE_APP_ENV || import.meta.env.MODE || 'development').trim();
  const version = String(import.meta.env.VITE_APP_VERSION || '0.1.0').trim();
  const sha = String(import.meta.env.VITE_FST_RELEASE || import.meta.env.VITE_RELEASE_SHA || 'dev').trim();
  const buildDate = String(import.meta.env.VITE_APP_BUILD_DATE || 'unknown').trim();
  return { slug, env, version, sha, buildDate };
}

export function formatRuntimeBadgeLabel() {
  const info = getRuntimeInfo();
  const env = info.env === 'production' ? 'prod' : info.env;
  const shortSha = info.sha === 'dev' ? 'dev' : info.sha.slice(0, 7);
  return `${env} · v${info.version} · ${shortSha}`;
}

export function formatRuntimeBadgeTitle() {
  const info = getRuntimeInfo();
  return `${info.slug} · ${info.env} · ${info.version} · ${info.sha} · ${info.buildDate}`;
}

export function jobStatusClass(status: string): string {
  if (status === 'Confirmé') return 'confirm';
  return status.toLowerCase().replace(/[^a-z]+/g, '-').replace(/^-+|-+$/g, '');
}

export function getPhoneStatus(flowState: FlowState): { label: string; className: string } {
  if (flowState.syncStatus === 'conflict') {
    return { label: 'Conflit', className: 'status-pill status-pill--danger' };
  }
  if (flowState.syncStatus === 'retrying') {
    return { label: 'Relance', className: 'status-pill status-pill--warning' };
  }
  if (flowState.reportStatus === 'rejected') {
    return { label: 'Reprise', className: 'status-pill status-pill--warning' };
  }
  if (flowState.syncStatus === 'synced') {
    return { label: 'Synchronisée', className: 'status-pill status-pill--ready' };
  }
  return { label: 'Hors ligne', className: 'status-pill status-pill--offline' };
}

export function getSubmitButtonLabel(flowState: FlowState): string {
  if (flowState.reportStatus === 'rejected') return 'Renvoyer le rapport';
  if (flowState.reportStatus === 'submitted') return 'Rapport envoyé';
  if (flowState.reportStatus === 'resubmitted') return 'Rapport re-soumis';
  if (flowState.reportStatus === 'validated') return 'Rapport validé';
  return 'Envoyer le rapport';
}

export function isSubmitDisabled(flowState: FlowState): boolean {
  return (
    flowState.syncStatus === 'conflict' ||
    flowState.syncStatus === 'retrying' ||
    flowState.reportStatus === 'submitted' ||
    flowState.reportStatus === 'resubmitted' ||
    flowState.reportStatus === 'validated'
  );
}

export function getPhoneFrameStatusText(flowState: FlowState): string {
  if (flowState.syncStatus === 'conflict') return 'Conflit détecté · Action requise';
  if (flowState.syncStatus === 'retrying') return 'Relance de synchronisation';
  if (flowState.syncStatus === 'synced') return 'Données alignées · Prêtes à envoyer';
  if (flowState.reportStatus === 'rejected') return 'Reprise terrain · Correction requise';
  return 'Brouillon hors ligne · Synchronisation en attente';
}

export function roleLabel(role: string | null | undefined): string {
  switch (role) {
    case 'admin':
      return 'Administrateur';
    case 'dispatch':
      return 'Planification';
    case 'technician':
      return 'Technicien';
    case 'backoffice':
      return 'Exploitation';
    default:
      return 'Opérateur';
  }
}

export function displayNameLabel(name: string | null | undefined): string | null {
  if (!name) return null;
  if (name === 'Dispatch FST') return 'Planification FST';
  if (name === 'Back-office FST') return 'Exploitation FST';
  return name;
}

export function formatDateTimeLabel(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat('fr-FR', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

export function formatHumanDateTimeLabel(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  const dateFormatter = new Intl.DateTimeFormat('fr-FR', {
    day: '2-digit',
    month: 'short',
  });
  const timeFormatter = new Intl.DateTimeFormat('fr-FR', {
    hour: '2-digit',
    minute: '2-digit',
  });

  const now = new Date();
  const isSameDay =
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate();

  return isSameDay ? `Aujourd'hui, ${timeFormatter.format(date)}` : `${dateFormatter.format(date)} · ${timeFormatter.format(date)}`;
}

export function formatScheduleDateLabel(value: Date | string): string {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return typeof value === 'string' ? value : '';

  const today = new Date();
  const startOfDay = (input: Date) => new Date(input.getFullYear(), input.getMonth(), input.getDate());
  const diffDays = Math.round((startOfDay(date).getTime() - startOfDay(today).getTime()) / 86_400_000);

  if (diffDays === 0) return "Aujourd'hui";
  if (diffDays === -1) return 'Hier';
  if (diffDays === 1) return 'Demain';

  return new Intl.DateTimeFormat('fr-FR', {
    weekday: 'long',
    day: '2-digit',
    month: 'long',
  }).format(date);
}

export function parseCurrencyLabel(value: string): number {
  const normalized = value.replace(/\s+/g, '').replace('€', '').replace(',', '.');
  const parsed = Number.parseFloat(normalized);
  if (Number.isNaN(parsed)) return 0;
  return Math.round(parsed * 100);
}

export function getBillingItemClass(stage: string): string {
  if (stage === 'ready') return ' billing-item--ready';
  if (stage === 'blocked') return ' billing-item--blocked';
  if (stage === 'review' || stage === 'field') return ' billing-item--warning';
  return '';
}

export function billingViewLabel(view: BillingPipelineView): string {
  switch (view) {
    case 'ready':
      return 'Prêts à facturer';
    case 'blocked':
      return 'Bloqués';
    case 'invoiced':
      return 'Facturés';
    case 'all':
      return 'Tous les dossiers';
    default:
      return 'File active';
  }
}

export function matchesBillingSearch(item: BillingPipelineItem, query: string): boolean {
  const normalizedQuery = query.trim().toLowerCase();
  if (!normalizedQuery) return true;
  return [
    item.workOrderId,
    item.clientName,
    item.siteName,
    item.technicianName ?? '',
    item.billingStageLabel,
    item.recommendedAction,
    item.invoiceNumber ?? '',
  ].some((value) => value.toLowerCase().includes(normalizedQuery));
}
